import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal.jsx";
import EarlyExitBreakdown from "../components/EarlyExitBreakdown.jsx";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useChainTime } from "../hooks/useChainTime.js";
import { useDeposits, DEPOSIT_STATE } from "../hooks/useDeposits.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { fetchTransactionsByOwner } from "../services/api.js";
import { decodeContractError } from "../utils/contractError.js";
import {
    depositStateLabel,
    formatCountdown,
    formatUsdcCompact,
    liveAccruedInterestSmooth,
    displayAprLabel
} from "../utils/format.js";

const TOKEN_SYMBOL = "USDC";

/**
 * Product options for the rollover — mirrors Deposit.jsx's PRODUCT_OPTIONS
 * so the rollover Product selector looks and behaves identically.
 */
const ROLLOVER_PRODUCT_OPTIONS = [
    {
        value: 2,
        label: "Turbo",
        rate: "up to 27.5%",
        hint: "Recommended",
        copy: "Turbo: interest is leveraged back into new deposits for maximum yield."
    },
    {
        value: 1,
        label: "Standard",
        rate: "20%",
        hint: "80% Principal Floor",
        copy: "Standard: yield reinvests linearly. No leverage."
    }
];

/**
 * Single-deposit detail page reached from the "View More" action on the
 * My TD's table. Pulls the row from the same `useDeposits` hook the list
 * uses so the data stays consistent (no extra fetch) and any optimistic
 * patches (early exit, settle) immediately reflect here too.
 */
export default function TdDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { address, signer } = useWallet();
    const { deposits, isLoading, refresh, patchDeposit } = useDeposits(address);

    const depositId = Number(id);
    const deposit = useMemo(
        () => deposits.find((d) => d.id === depositId) || null,
        [deposits, depositId]
    );

    const [exitOpen, setExitOpen] = useState(false);
    const [exitBusy, setExitBusy] = useState(false);
    const [exitFlowOpen, setExitFlowOpen] = useState(false);
    const [exitSteps, setExitSteps] = useState([]);
    const [result, setResult] = useState(null);
    const [rolloverOpen, setRolloverOpen] = useState(false);
    // true → compound principal + interest into the new deposit;
    // false → roll only principal, take interest as USDC.
    const [rolloverCompound, setRolloverCompound] = useState(true);
    // Product choice for the NEW deposit (UI enum: 2=Turbo, 1=Standard).
    // Pre-filled from the current deposit's productMode so the user's
    // previous product carries forward; still fully editable in the modal.
    // Contract productMode 1=Turbo → UI 2; contract 0=Standard → UI 1.
    const [rolloverProduct, setRolloverProduct] = useState(2);
    // Automated Reinvestment ON/OFF toggle for the new deposit.
    const [rolloverAutoCompound, setRolloverAutoCompound] = useState(true);
    // Derived compoundMode (0/1/2) that feeds downstream setCompoundMode logic.
    const rolloverCompoundMode = rolloverAutoCompound ? rolloverProduct : 0;

    // Pre-fill rolloverProduct from the current deposit once it loads.
    // Contract productMode: 1=Turbo → UI 2, 0=Standard → UI 1. Falls back to
    // UI 2 (Turbo) when unavailable — same default as Deposit.jsx.
    useEffect(() => {
        if (deposit) {
            const contractPm = Number(deposit.productMode);
            setRolloverProduct(contractPm === 1 ? 2 : contractPm === 0 ? 1 : 2);
        }
    }, [deposit?.productMode]);

    // Chain-time hook — MUST be called unconditionally before any of
    // the early returns below (Rules of Hooks). Drives the live
    // maturity countdown so the local Hardhat node fast-forward shows
    // through immediately.
    const { chainNowSec } = useChainTime();

    // Project the maturity payout using the same simple-interest math the
    // contract enforces. Declared up-front (BEFORE any early returns)
    // because React's rules of hooks require the same hooks to run on
    // every render — putting this `useMemo` after a conditional return
    // would change the hook count when the deposit appears or
    // disappears, which is what triggered the "order of Hooks" warning.
    const projectedInterest = useMemo(() => {
        if (!deposit) return 0n;
        const principal = BigInt(deposit.principal || 0);
        // §V6.3 — preview at the HEADLINE rate so a Turbo deposit shows
        // Turbo numbers (up-to-27.5%), matching the APR badge and the
        // Deposit-page preview. Standard uses its stored 20% rate. On-chain
        // each rung still accrues 20%; the net 27.5% emerges from the
        // leverage loop, so this is a display-only projection.
        const rateBps = Number(deposit.productMode) === 1
            ? 2750
            : (Number(deposit.annualRateBps) || config.protocol.annualRateBps);
        const rate = BigInt(rateBps);
        const days = BigInt(config.protocol.fixedTermDays);
        return (principal * rate * days) / (10000n * 365n);
    }, [deposit]);

    function setStepStatus(key, status) {
        setExitSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    async function settleMatured() {
        if (!signer || !deposit) return;
        setExitBusy(true);
        setExitSteps([
            {
                key: "settle",
                label: `Settling Term Deposit #${deposit.id}`,
                doneLabel: `Settled Term Deposit #${deposit.id}`,
                status: "loading"
            }
        ]);
        setExitFlowOpen(true);
        setResult(null);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.settleMatured(deposit.id, overrides);
            await tx.wait();
            setStepStatus("settle", "done");
            patchDeposit(deposit.id, { state: DEPOSIT_STATE.Closed });
            setExitFlowOpen(false);
            setResult({
                type: "success",
                title: "Settlement Complete",
                message: `Term Deposit #${deposit.id} settled. Principal and accrued interest returned to your wallet.`
            });
            refresh().catch(() => {});
        } catch (err) {
            setStepStatus("settle", "error");
            setExitFlowOpen(false);
            setResult({
                type: "error",
                title: "Settlement Failed",
                message: decodeContractError(err)
            });
        } finally {
            setExitBusy(false);
        }
    }

    /**
     * Spec §4.6 / §5: rolls a matured (or in-grace Dormant) deposit into
     * a fresh 3-year term deposit in one transaction. `rolloverCompound`
     * decides whether accrued interest compounds in or is paid out.
     */
    async function handleRollover() {
        if (!signer || !deposit) return;
        setExitBusy(true);
        // Build the step list dynamically — the Automated Reinvestment step is
        // skipped when the user picked Off, mirroring the Deposit flow.
        const initialSteps = [
            {
                key: "rollover",
                label: `Rolling over Term Deposit #${deposit.id}`,
                doneLabel: `Rolled over Term Deposit #${deposit.id}`,
                status: "loading"
            }
        ];
        if (rolloverCompoundMode !== 0) {
            const modeLabel = rolloverCompoundMode === 2 ? "Turbo" : "Standard";
            initialSteps.push({
                key: "compound",
                label: `Enabling ${modeLabel} Automated Reinvestment`,
                doneLabel: `${modeLabel} Automated Reinvestment enabled`,
                status: "pending"
            });
        }
        setExitSteps(initialSteps);
        setRolloverOpen(false);
        setExitFlowOpen(true);
        setResult(null);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            // §V6.2 — new 3rd arg: contract product enum (0=Standard, 1=Turbo).
            const contractProductMode = rolloverProduct === 2 ? 1 : 0;
            const tx = await bond.rollover(deposit.id, rolloverCompound, contractProductMode, overrides);
            const receipt = await tx.wait();
            setStepStatus("rollover", "done");

            let newId = null;
            for (const log of receipt.logs) {
                try {
                    const parsed = bond.interface.parseLog(log);
                    if (parsed?.name === "DepositRolledOver") {
                        newId = Number(parsed.args.newDepositId);
                        break;
                    }
                } catch {
                    /* not a Bond log — skip */
                }
            }

            // Apply the user's Automated Reinvestment preference to the freshly-
            // minted deposit. Skipped when the user opted Off. Mirrors
            // Deposit.jsx — same race guard (waitForDepositVisible) keeps
            // setCompoundMode from estimating against a stale RPC head.
            // Non-fatal: rollover already succeeded so we surface a soft
            // warning rather than failing the whole flow.
            let compoundWarning = "";
            if (rolloverCompoundMode !== 0 && newId) {
                setStepStatus("compound", "loading");
                try {
                    await waitForDepositVisible(bond, newId);
                    // §I.6 V6.2 — setCompoundMode lives on the BondContract;
                    // the standalone AutoCompound contract is deprecated.
                    const cmTx = await bond.setCompoundMode(
                        newId,
                        rolloverCompoundMode,
                        overrides
                    );
                    await cmTx.wait();
                    setStepStatus("compound", "done");
                } catch (cmErr) {
                    console.warn("Automated Reinvestment enable failed:", cmErr?.message || cmErr);
                    setStepStatus("compound", "error");
                    compoundWarning =
                        " Automated Reinvestment could not be enabled automatically. Set it anytime from Settings.";
                }
            }

            patchDeposit(deposit.id, { state: DEPOSIT_STATE.Closed });
            setExitFlowOpen(false);
            setResult({
                type: "success",
                title: "Rollover Complete",
                message: (rolloverCompound
                    ? `Term Deposit #${deposit.id} closed and rolled into a new 3-year deposit${
                          newId ? ` (#${newId})` : ""
                      } — principal and accrued interest compounded into the new term.`
                    : `Term Deposit #${deposit.id} closed and its principal rolled into a new 3-year deposit${
                          newId ? ` (#${newId})` : ""
                      }. Accrued interest was returned to your wallet.`) + compoundWarning
            });
            refresh().catch(() => {});
        } catch (err) {
            setStepStatus("rollover", "error");
            setExitFlowOpen(false);
            setResult({
                type: "error",
                title: "Rollover Failed",
                message: decodeContractError(err)
            });
        } finally {
            setExitBusy(false);
        }
    }

    /**
     * Spec §5 Phase 3: an Abandoned deposit can still have its remaining
     * (decay-fee-depleted) SafeVault balance withdrawn at any time.
     */
    async function withdrawAbandoned() {
        if (!signer || !deposit) return;
        setExitBusy(true);
        setExitSteps([
            {
                key: "withdraw",
                label: `Withdrawing Term Deposit #${deposit.id}`,
                doneLabel: `Withdrawn Term Deposit #${deposit.id}`,
                status: "loading"
            }
        ]);
        setExitFlowOpen(true);
        setResult(null);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.withdrawAbandoned(deposit.id, overrides);
            await tx.wait();
            setStepStatus("withdraw", "done");
            patchDeposit(deposit.id, { state: DEPOSIT_STATE.Closed });
            setExitFlowOpen(false);
            setResult({
                type: "success",
                title: "Withdrawal Complete",
                message: `Term Deposit #${deposit.id} closed. The remaining balance of your Safe Vault slice has been returned to your wallet.`
            });
            refresh().catch(() => {});
        } catch (err) {
            setStepStatus("withdraw", "error");
            setExitFlowOpen(false);
            setResult({
                type: "error",
                title: "Withdrawal Failed",
                message: decodeContractError(err)
            });
        } finally {
            setExitBusy(false);
        }
    }

    async function confirmEarlyExit() {
        if (!signer || !deposit) return;
        setExitBusy(true);
        setExitSteps([
            {
                key: "exit",
                label: `Closing Term Deposit #${deposit.id}`,
                doneLabel: `Closed Term Deposit #${deposit.id}`,
                status: "loading"
            }
        ]);
        setExitFlowOpen(true);
        setResult(null);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.earlyExit(deposit.id, overrides);
            await tx.wait();
            setStepStatus("exit", "done");

            patchDeposit(deposit.id, { state: DEPOSIT_STATE.Exited });
            setExitFlowOpen(false);
            setExitOpen(false);
            // §V6.2 — per-product penalty (Standard 20%, Turbo 50%).
            const penaltyPct =
                (Number(deposit.earlyExitPenaltyBps) ||
                    (Number(deposit.productMode) === 1 ? 5000 : 2000)) / 100;
            setResult({
                type: "success",
                title: "Early Exit Complete",
                message: `Term Deposit #${deposit.id} has been closed. The ${penaltyPct}% penalty was routed to the Distribution Reserve; the remaining principal plus accrued interest has been returned to your wallet.`
            });
            refresh().catch(() => {});
        } catch (err) {
            setStepStatus("exit", "error");
            setExitFlowOpen(false);
            setResult({
                type: "error",
                title: "Early Exit Failed",
                message: decodeContractError(err)
            });
        } finally {
            setExitBusy(false);
        }
    }

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Connect your wallet to view this term deposit.
                    </p>
                </div>
            </div>
        );
    }

    if (isLoading && !deposit) {
        return <TdDetailSkeleton depositId={depositId} />;
    }

    if (!deposit) {
        return (
            <div className="-mt-6 flex min-h-[40vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Term Deposit Not Found</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Term Deposit #{depositId} doesn&rsquo;t exist or isn&rsquo;t owned by this wallet.
                    </p>
                    <Link
                        to="/term-deposits"
                        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
                    >
                        Back to My Term Deposits
                        <ChevronRight />
                    </Link>
                </div>
            </div>
        );
    }

    const rawStateNum = Number(deposit.state);
    const maturityReached = chainNowSec >= Number(deposit.maturityTime || 0);
    // Display state = the state the UI surfaces. The indexed `state`
    // only flips Active → Matured when someone explicitly calls
    // `markMatured()` on chain; between maturity reaching and that
    // call landing, the row reads Active even though the deposit IS
    // matured (the next settle/rollover tx will auto-transition).
    // The pill, the "State" infoRow and the action gates all key off
    // the DISPLAY state so the UI stays in sync with reality —
    // matches the same logic the MyTds tabs use.
    const stateNum = rawStateNum === DEPOSIT_STATE.Active && maturityReached
        ? DEPOSIT_STATE.Matured
        : rawStateNum;
    const stateLabel = depositStateLabel(stateNum);
    const isActive = stateNum === DEPOSIT_STATE.Active;
    const idPadded = String(deposit.id).padStart(6, "0");
    // A position is settle-eligible if the display state is
    // Matured or Dormant (the maturity-reached promotion above
    // already folds the "Active but past maturity" case into
    // Matured here).
    const canSettleNow =
        stateNum === DEPOSIT_STATE.Matured ||
        stateNum === DEPOSIT_STATE.Dormant;
    // Once maturity is reached we hide the Early Exit CTA — it
    // would still execute on-chain (the contract allows it) but
    // there's no reason to forfeit 20% of principal when the user
    // can Settle for the full principal + interest instead.
    const showEarlyExit = isActive && !maturityReached;

    const principal = BigInt(deposit.principal || 0);
    const loan = BigInt(deposit.loanBalance || 0);
    // Live-accrual replay keyed off the RAW (chain) state, not the
    // display state. While a deposit's chain `state` is still 1
    // (Active) — including the "matured but markMatured not yet
    // called" interval — the indexed `accruedInterest` reads 0 and
    // the formula must drive the figure. Only TRUE terminal states
    // (chain state 2/3/4/5/6) have a credited stored value to trust.
    // Accrued — per client (Jun 2026) this must update in REAL TIME (not
    // sit at $0.00 until a whole day ticks). Active deposits render the
    // per-second smoother; matured/terminal states trust the finalised
    // indexed value. Display-only — settlement / early-exit use the
    // day-stepped path so payouts are never overstated. Turbo shows its
    // headline 27.5% (matching the badge + At-Maturity projection).
    const depRateBps = Number(deposit.productMode) === 1
        ? 2750
        : (Number(deposit.annualRateBps) || config.protocol.annualRateBps);
    const accrued = rawStateNum === DEPOSIT_STATE.Active
        ? liveAccruedInterestSmooth(
              deposit.principal,
              deposit.startTime,
              deposit.maturityTime,
              chainNowSec,
              depRateBps,
              deposit.realizedInterest
          )
        : BigInt(deposit.accruedInterest || 0);
    // §V6.2 — capital routing is 80/10/9/1 for BOTH products now
    // (Turbo differs only by APR and early-exit penalty). Prefer the
    // chain-stored on-deposit amounts (immutable at open), fall back to
    // the 80/10/9/1 bps when the indexer hasn't synced them yet.
    const isTurboProduct = Number(deposit.productMode) === 1;
    const productSafeVaultBps = 8000n;
    const productTokenBuyBps  = 1000n;
    const productTreasuryBps  =  900n;
    const productLiquidityBps =  100n;
    const safeVault = BigInt(deposit.safeVaultAmount || 0) > 0n
        ? BigInt(deposit.safeVaultAmount)
        : (principal * productSafeVaultBps) / 10000n;
    const tokenBuy = BigInt(deposit.tokenBuyAmount || 0) > 0n
        ? BigInt(deposit.tokenBuyAmount)
        : (principal * productTokenBuyBps) / 10000n;
    const treasury = BigInt(deposit.treasuryAmount || 0) > 0n
        ? BigInt(deposit.treasuryAmount)
        : (principal * productTreasuryBps) / 10000n;
    const liquidity = BigInt(deposit.liquidityAmount || 0) > 0n
        ? BigInt(deposit.liquidityAmount)
        : (principal * productLiquidityBps) / 10000n;
    // Display labels for the routing strip — 80/10/9/1 for both
    // products. Computed from the same bps so a future tweak only
    // needs to change the constants above.
    const routeStrip = `${Number(productSafeVaultBps) / 100} / ${Number(productTokenBuyBps) / 100} / ${Number(productTreasuryBps) / 100} / ${Number(productLiquidityBps) / 100}`;
    const safeVaultPctLabel = `${Number(productSafeVaultBps) / 100}%`;
    const tokenBuyPctLabel = `${Number(productTokenBuyBps) / 100}%`;
    const treasuryPctLabel = `${Number(productTreasuryBps) / 100}%`;
    const liquidityPctLabel = `${Number(productLiquidityBps) / 100}%`;
    // Bar widths use raw bps fraction so fractional legs (e.g. 9%, 1%)
    // render proportionally without rounding.
    const safeVaultBarPct = Number(productSafeVaultBps) / 100;
    const tokenBuyBarPct = Number(productTokenBuyBps) / 100;
    const treasuryBarPct = Number(productTreasuryBps) / 100;
    const liquidityBarPct = Number(productLiquidityBps) / 100;

    // `projectedInterest` was computed above (with `useMemo`) so the
    // hook count stays stable across renders. `projectedTotal` is just
    // a derivation, no hook needed.
    const projectedTotal = principal + projectedInterest;

    // `chainNowSec` from the top-of-component `useChainTime()` call.
    // Keeps remaining-time + progress-bar accurate when the local
    // Hardhat node has been fast-forwarded via evm_increaseTime.
    const now = chainNowSec;
    const remaining = Math.max(0, Number(deposit.maturityTime) - now);
    const elapsed = Math.max(0, now - Number(deposit.startTime));
    const term = Number(deposit.maturityTime) - Number(deposit.startTime);
    const progressPct = term > 0 ? Math.min(100, Math.round((elapsed / term) * 100)) : 0;

    const explorerBase = config.network.blockExplorerUrl?.replace(/\/+$/, "");
    const txHash = deposit.txHash;
    const explorerTxUrl = explorerBase && txHash ? `${explorerBase}/tx/${txHash}` : null;
    const explorerNftUrl = explorerBase && config.contracts.depositNft
        ? `${explorerBase}/token/${config.contracts.depositNft}?a=${deposit.id}`
        : null;

    return (
        <div className="-mt-6 space-y-3">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-baseline gap-3">
                    <StatePill state={stateNum} label={stateLabel} />
                </div>
                <div className="flex items-center gap-2">
                    {explorerTxUrl ? (
                        <a
                            href={explorerTxUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#10281b] px-3 py-1 text-[11px] font-semibold text-white ring-1 ring-[#bfb67b] hover:bg-[#162e1f]"
                        >
                            View on Explorer
                            <ExternalIcon />
                        </a>
                    ) : null}
                    {showEarlyExit ? (
                        <button
                            type="button"
                            onClick={() => setExitOpen(true)}
                            disabled={!signer || loan > 0n}
                            title={loan > 0n ? "Repay outstanding loan before early exit" : undefined}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#C93030] px-3.5 py-1 text-[11px] font-semibold text-white hover:bg-[#A82626] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ExitIcon />
                            Early Exit
                        </button>
                    ) : null}
                    {canSettleNow ? (
                        <button
                            type="button"
                            onClick={settleMatured}
                            disabled={!signer || exitBusy || loan > 0n}
                            title={loan > 0n ? "Repay outstanding loan before settling" : undefined}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#FBC206] px-3.5 py-1 text-[11px] font-semibold text-[#10281b] hover:bg-[#e6b005] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Settle & Withdraw
                        </button>
                    ) : null}
                    {stateNum === DEPOSIT_STATE.Abandoned ? (
                        <button
                            type="button"
                            onClick={withdrawAbandoned}
                            disabled={!signer || exitBusy || loan > 0n}
                            title={loan > 0n ? "Repay outstanding loan before withdrawing" : "Withdraw the remaining balance of this abandoned deposit"}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#FBC206] px-3.5 py-1 text-[11px] font-semibold text-[#10281b] hover:bg-[#e6b005] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Withdraw Remaining
                        </button>
                    ) : null}
                    {canSettleNow ? (
                        <button
                            type="button"
                            onClick={() => setRolloverOpen(true)}
                            disabled={!signer || exitBusy || loan > 0n}
                            title={
                                loan > 0n
                                    ? "Repay outstanding loan before rolling over"
                                    : "Roll this matured deposit into a fresh 3-year term"
                            }
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#1D3E29] px-3.5 py-1 text-[11px] font-semibold text-white hover:bg-[#0E2C19] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Rollover
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Hero strip — large numbers */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 text-white flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Position Snapshot</h3>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-[#FBC206]">
                            {config.protocol.fixedTermDays}d · {displayAprLabel(deposit)}% APR
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        <Metric label="Principal" value={`$${formatUsdcCompact(principal)}`} hint="Current" />
                        <Metric label="Accrued" value={`$${formatUsdcCompact(accrued)}`} hint="So far" accent />
                        <Metric label="At Maturity" value={`$${formatUsdcCompact(projectedTotal)}`} hint="Projected" bold />
                    </div>
                </div>

                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Time to Maturity</h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            {/* Closed-early / settled positions don't have a
                                meaningful "% Elapsed" — the position ended on
                                the user's terms, not on the clock's. */}
                            {stateNum === DEPOSIT_STATE.Exited
                                ? "Closed early"
                                : stateNum === DEPOSIT_STATE.Closed
                                    ? "Settled"
                                    : `${progressPct}% Elapsed`}
                        </span>
                    </div>
                    <div className="mt-3">
                        <div className="font-manrope text-[24px] font-bold text-white">
                            {/* State takes priority over the time math: an
                                Exited deposit never matured — it was force-
                                closed by the owner. Showing "Matured" because
                                the clock has rolled past `maturityTime` would
                                misrepresent what actually happened. */}
                            {stateNum === DEPOSIT_STATE.Exited
                                ? "Exited"
                                : stateNum === DEPOSIT_STATE.Closed
                                    ? "Closed"
                                    : remaining > 0
                                        ? formatCountdown(remaining)
                                        : "Matured"}
                        </div>
                        <div className="mt-0.5 text-[10px] text-[#e6c985]/70">
                            {/* For terminal states show the ACTUAL close
                                date — `maturityTime` is meaningless for an
                                early-exited position (the term clock
                                stopped at exit, not at the original
                                3-year mark). Falls back to maturityTime
                                only if `closedAt` is missing (pre-this-fix
                                row that the indexer hasn't refreshed yet). */}
                            {(stateNum === DEPOSIT_STATE.Exited ||
                                stateNum === DEPOSIT_STATE.Closed) &&
                            deposit.closedAt
                                ? `${stateNum === DEPOSIT_STATE.Exited ? "Exited" : "Closed"}: ${formatDate(deposit.closedAt)}`
                                : `Maturity: ${formatDate(deposit.maturityTime)}`}
                        </div>
                    </div>
                    {stateNum === DEPOSIT_STATE.Exited ||
                    stateNum === DEPOSIT_STATE.Closed ? (
                        // Progress bar is meaningless for closed-out
                        // positions — the term clock stopped at exit /
                        // settlement, and we don't keep the exit timestamp
                        // on the indexed row to draw a partial bar
                        // accurately. Showing the live bar would keep
                        // ticking with the chain clock and falsely
                        // suggest the term was "fully elapsed".
                        <div className="mt-3 text-[10px] italic text-[#e6c985]/60">
                            Position closed — term clock stopped.
                        </div>
                    ) : (
                        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10 ring-1 ring-[#bfb67b]">
                            <div
                                className="h-full rounded-full bg-[#FBC206] transition-all"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Detail grid */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                {/* Capital Routing */}
                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Capital Routing</h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            {routeStrip}
                        </span>
                    </div>
                    <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full ring-1 ring-[#bfb67b]">
                        <span className="h-full" style={{ width: `${safeVaultBarPct}%`, backgroundColor: "#1D3E29" }} />
                        <span className="h-full" style={{ width: `${tokenBuyBarPct}%`, backgroundColor: "#FBC206" }} />
                        <span className="h-full" style={{ width: `${treasuryBarPct}%`, backgroundColor: "#5C7A66" }} />
                        <span className="h-full" style={{ width: `${liquidityBarPct}%`, backgroundColor: "#bfb67b" }} />
                    </div>
                    <ul className="mt-4 space-y-3">
                        <RouteRow color="#1D3E29" label="Safe Vault" pct={safeVaultPctLabel} amount={formatUsdcCompact(safeVault)} />
                        <RouteRow color="#FBC206" label="Token Buy" pct={tokenBuyPctLabel} amount={formatUsdcCompact(tokenBuy)} />
                        <RouteRow color="#5C7A66" label="Distribution Reserve" pct={treasuryPctLabel} amount={formatUsdcCompact(treasury)} />
                        <RouteRow color="#bfb67b" label="Liquidity" pct={liquidityPctLabel} amount={formatUsdcCompact(liquidity)} last />
                    </ul>
                </div>

                {/* Lifecycle — for live (Active / Matured / Dormant /
                    Abandoned) positions we show the full forward
                    timeline (Opened → Maturity → Grace Cliff). For
                    terminal states (Exited / Closed) the position
                    ended on the owner's terms, so we collapse to
                    Opened → <terminal event> and drop the future
                    milestones that no longer apply. */}
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Lifecycle</h3>
                    <ol className="mt-4 space-y-3">
                        <TimelineRow
                            label="Opened"
                            timestamp={deposit.startTime}
                            done
                            description="Capital routed atomically across the four destinations."
                        />
                        {stateNum === DEPOSIT_STATE.Exited ? (
                            <TimelineRow
                                label="Early Exit"
                                timestamp={deposit.closedAt}
                                done
                                description={`Owner force-closed the position before maturity. ${(Number(deposit.earlyExitPenaltyBps) || (isTurboProduct ? 5000 : 2000)) / 100}% penalty was routed to the Distribution Reserve; the remaining principal + accrued interest were returned to the wallet. No further milestones apply.`}
                                last
                            />
                        ) : stateNum === DEPOSIT_STATE.Closed ? (
                            <TimelineRow
                                label="Settled / Closed"
                                timestamp={deposit.closedAt}
                                done
                                description="Position settled, rolled over, liquidated, or recovered from Abandoned. Principal + interest fully accounted for. No further milestones apply."
                                last
                            />
                        ) : (
                            <>
                                <TimelineRow
                                    label="Maturity"
                                    timestamp={deposit.maturityTime}
                                    done={
                                        stateNum === DEPOSIT_STATE.Matured ||
                                        stateNum === DEPOSIT_STATE.Dormant ||
                                        stateNum === DEPOSIT_STATE.Abandoned ||
                                        now >= Number(deposit.maturityTime)
                                    }
                                    description="Principal plus accrued interest become claimable via Settle."
                                />
                                <TimelineRow
                                    label="Grace Cliff"
                                    timestamp={Number(deposit.maturityTime) + 365 * 24 * 60 * 60}
                                    done={
                                        stateNum === DEPOSIT_STATE.Abandoned ||
                                        now >=
                                            Number(deposit.maturityTime) + 365 * 24 * 60 * 60
                                    }
                                    description="After 12 months without a settle, deposit transitions to Abandoned. A 5%/month maintenance fee is then deducted from the Safe Vault portion."
                                    last
                                />
                            </>
                        )}
                    </ol>
                </div>
            </div>

            {/* Position info + auto-compound + loan */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Position Info</h3>
                    <ul className="mt-3 divide-y divide-white/10">
                        <InfoRow label="Deposit ID" value={`#${idPadded}`} />
                        <InfoRow label="Owner" value={shortAddr(deposit.owner)} mono />
                        <InfoRow label="State" value={stateLabel} />
                        {/* §V6.2 — productMode is the immutable product
                            (Standard / Turbo) chosen at deposit open. Shown
                            on its own row so the user can audit which
                            economics (APR / routing / penalty) apply. */}
                        <InfoRow
                            label="Product"
                            value={isTurboProduct ? "Turbo" : "Standard"}
                        />
                        {/* Compound Mode is the user-toggleable On/Off
                            flag — when On, auto-compound fires once the
                            position crosses the $100 threshold. The
                            product (set above) decides whether the
                            compound is Standard or Turbo flavoured;
                            this row just answers "is auto-compound
                            switched on for this deposit?". */}
                        <InfoRow
                            label="Automated Reinvestment"
                            value={Number(deposit.compoundMode) > 0 ? "On" : "Off"}
                        />
                        <InfoRow
                            label="NFT Token"
                            value={
                                explorerNftUrl ? (
                                    <a
                                        href={explorerNftUrl}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="inline-flex items-center gap-1 font-semibold text-white hover:underline"
                                    >
                                        #{deposit.id}
                                        <ExternalIcon />
                                    </a>
                                ) : (
                                    `#${deposit.id}`
                                )
                            }
                        />
                        <InfoRow
                            label="Open Tx"
                            value={
                                explorerTxUrl ? (
                                    <a
                                        href={explorerTxUrl}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="inline-flex items-center gap-1 font-mono text-[10.5px] font-semibold text-white hover:underline"
                                    >
                                        {txHash ? `${txHash.slice(0, 8)}…${txHash.slice(-6)}` : "—"}
                                        <ExternalIcon />
                                    </a>
                                ) : txHash ? (
                                    <span className="font-mono text-[10.5px]">{txHash.slice(0, 12)}…</span>
                                ) : (
                                    <span className="text-[#e6c985]/60">unavailable</span>
                                )
                            }
                        />
                        <InfoRow last label="Network" value={config.network.name} />
                    </ul>
                </div>

                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Loan Position</h3>
                        {loan > 0n ? (
                            <Link
                                to={`/loans/${deposit.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[#FBC206] px-3 py-1 text-[10px] font-semibold text-[#10281b] hover:bg-[#e6b005] transition-colors"
                            >
                                View Loan
                                <ChevronRight />
                            </Link>
                        ) : null}
                    </div>
                    {loan > 0n ? (
                        <Link to={`/loans/${deposit.id}`} className="mt-3 block space-y-2 group">
                            <div className="rounded-[8px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 px-3 py-2 group-hover:ring-[#FBC206]/60 transition-colors">
                                <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                                    Outstanding Balance
                                </div>
                                <div className="mt-0.5 font-manrope text-[15px] font-bold text-white">
                                    ${formatUsdcCompact(loan)} {TOKEN_SYMBOL}
                                </div>
                            </div>
                            <p className="text-[10.5px] leading-snug text-[#e6c985]/80">
                                Borrowed against this deposit&rsquo;s Safe Vault portion. Interest accrues
                                continuously and must be repaid before settlement.{" "}
                                <span className="font-semibold text-[#FBC206] group-hover:underline">
                                    View loan details &rarr;
                                </span>
                            </p>
                        </Link>
                    ) : (
                        <div className="mt-3 flex flex-1 flex-col items-center justify-center text-center">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1D3E29] text-[#FBC206]">
                                <ShieldCheckIcon />
                            </span>
                            <div className="mt-2 font-manrope text-[13px] font-semibold text-white">
                                No active loan
                            </div>
                            {/* §V6.2 — Turbo deposits expose no fresh borrow
                                capacity (the product already deploys the
                                Safe Vault slice as leverage at creation),
                                so we hide the "Borrow up to X%" copy and
                                the Create Loan CTA. Mirrors the contract's
                                NoBorrowingAgainstTurbo revert + the
                                Lending page's Turbo filter — same surface,
                                same story everywhere. */}
                            {isTurboProduct ? (
                                <p className="mt-1 text-[10.5px] text-[#e6c985]/70 max-w-[18rem] text-center">
                                    Turbo deposits are fully deployed at creation — no
                                    additional borrow capacity is available against this
                                    position.
                                </p>
                            ) : (
                                <>
                                    <p className="mt-1 text-[10.5px] text-[#e6c985]/70">
                                        Borrow up to {config.protocol.maxLtvBps / 100}% of principal against this position.
                                    </p>
                                    <BorrowMenu />
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* §V6.2 — per-deposit activity feed. Every withdrawal,
                compound, sweep, loan event tagged to this depositId so
                the user can audit exactly how much capital has moved
                in / out of this specific position. */}
            <DepositActivity depositId={deposit.id} address={address} />

            <ConfirmModal
                open={exitOpen}
                title={`Confirm Early Exit – Term Deposit #${deposit.id}`}
                body={<EarlyExitBreakdown deposit={deposit} />}
                confirmLabel="Yes, Early Exit"
                cancelLabel="Cancel"
                tone="danger"
                busy={exitBusy}
                onConfirm={confirmEarlyExit}
                onCancel={() => (exitBusy ? null : setExitOpen(false))}
            />

            <ConfirmModal
                open={rolloverOpen}
                title={`Rollover – Term Deposit #${deposit.id}`}
                body={
                    <RolloverChoice
                        principal={principal}
                        accrued={accrued}
                        compound={rolloverCompound}
                        onChange={setRolloverCompound}
                        product={rolloverProduct}
                        onProductChange={setRolloverProduct}
                        autoCompound={rolloverAutoCompound}
                        onAutoCompoundChange={setRolloverAutoCompound}
                    />
                }
                confirmLabel="Confirm Rollover"
                cancelLabel="Cancel"
                busy={exitBusy}
                onConfirm={handleRollover}
                onCancel={() => (exitBusy ? null : setRolloverOpen(false))}
            />

            <FlowModal
                open={exitFlowOpen}
                title="Closing Deposit"
                subtitle="Confirm the wallet prompt to broadcast the early-exit transaction."
                steps={exitSteps}
            />

            <ResultModal
                open={result !== null}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                primaryAction={
                    result?.type === "success" ? { label: "Back to My Term Deposits", onClick: () => navigate("/term-deposits") } : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/**
 * §V6.2 — per-deposit activity feed. Shows every Transaction row
 * filtered by `depositId` so the user can audit the cash flow on a
 * SPECIFIC position: opens, settlements, early exits, auto-compounds
 * (independent or pool sweep), loans, repayments, NFT transfers.
 *
 * Each row labels its direction (in/out for the user), the absolute
 * USDC amount, and a tx-hash link to the block explorer. Useful for:
 *   - Verifying an Automated Reinvestment actually fired ("did my interest
 *     get reinvested or is it just sitting?")
 *   - Auditing how much capital has been withdrawn vs deposited
 *   - Diagnosing stuck states (an Automated Reinvestment entry should drop
 *     the live accrued display; if it's missing, the indexer or
 *     keeper has a gap)
 */
function DepositActivity({ depositId, address }) {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!depositId || !address) return undefined;
        let cancelled = false;
        async function load() {
            try {
                const data = await fetchTransactionsByOwner(address, {
                    depositId,
                    limit: 100
                });
                if (!cancelled) {
                    setItems(Array.isArray(data?.items) ? data.items : []);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) setError(err?.message || "Failed to load activity");
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [depositId, address]);

    return (
        <section className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">
                    Activity
                </h3>
                <span className="text-[10px] text-[#e6c985]/70">
                    On-chain history for Deposit #{depositId}
                </span>
            </div>

            {items === null && !error ? (
                <div className="mt-3 space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Shimmer key={`act-skel-${i}`} width="100%" height="2.5rem" />
                    ))}
                </div>
            ) : null}

            {error ? (
                <p className="mt-3 text-[11px] text-[#ff8a8a]">
                    Couldn&apos;t load activity: {error}
                </p>
            ) : null}

            {items !== null && items.length === 0 && !error ? (
                <p className="mt-3 text-[11px] text-[#e6c985]/70">
                    No on-chain events yet beyond the original open. Automated Reinvestment,
                    sweeps, and settlements will appear here once they fire.
                </p>
            ) : null}

            {items !== null && items.length > 0 ? (
                <ul className="mt-3 divide-y divide-white/10 rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/40">
                    {items.map((row, idx) => (
                        <ActivityRow key={`${row.txHash}-${row.logIndex}-${idx}`} row={row} />
                    ))}
                </ul>
            ) : null}
        </section>
    );
}

function ActivityRow({ row }) {
    const explorerBase = config.network.blockExplorerUrl?.replace(/\/+$/, "");
    const txLink = explorerBase && row.txHash ? `${explorerBase}/tx/${row.txHash}` : null;
    const isIn = row.direction === "in";
    const sign = isIn ? "+" : "−";
    const amountClass = isIn ? "text-[#22C55E]" : "text-[#ff8a8a]";
    const amount = row.amount ? formatUsdcCompact(BigInt(row.amount)) : "0";
    const when = row.timestamp ? new Date(row.timestamp) : null;
    const whenLabel = when
        ? when.toLocaleString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
          })
        : "—";
    const label = row.label || prettifyType(row.type);

    return (
        <li className="flex items-start justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="font-manrope text-[12px] font-semibold text-white">
                        {label}
                    </span>
                    <ActivityTypeTag type={row.type} />
                </div>
                <div className="mt-0.5 text-[10px] text-[#e6c985]/65">{whenLabel}</div>
                {row.metadata && Object.keys(row.metadata).length > 0 ? (
                    <ActivityMetadata metadata={row.metadata} />
                ) : null}
            </div>
            <div className="text-right">
                <div className={`font-manrope text-[12.5px] font-bold ${amountClass}`}>
                    {sign}${amount}
                </div>
                {txLink ? (
                    <a
                        href={txLink}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-0.5 inline-block text-[9.5px] text-[#FBC206] hover:underline"
                    >
                        tx ↗
                    </a>
                ) : null}
            </div>
        </li>
    );
}

function ActivityTypeTag({ type }) {
    const tone = TYPE_TONES[type] || "bg-white/10 text-[#e6c985]/85";
    return (
        <span className={`rounded-full px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest ${tone}`}>
            {(type || "").replace(/_/g, " ")}
        </span>
    );
}

function ActivityMetadata({ metadata }) {
    const fields = [];
    if (metadata.keeperFee && metadata.keeperFee !== "0") {
        fields.push(`Keeper fee $${formatUsdcCompact(BigInt(metadata.keeperFee))}`);
    }
    if (metadata.leverageBorrowed && metadata.leverageBorrowed !== "0") {
        fields.push(`Leverage $${formatUsdcCompact(BigInt(metadata.leverageBorrowed))}`);
    }
    if (metadata.newDepositId) {
        fields.push(`→ Deposit #${metadata.newDepositId}`);
    }
    if (metadata.poolId) {
        fields.push(`Pool #${metadata.poolId}`);
    }
    if (metadata.penalty) {
        fields.push(`Penalty $${formatUsdcCompact(BigInt(metadata.penalty))}`);
    }
    if (fields.length === 0) return null;
    return (
        <div className="mt-1 text-[9.5px] text-[#e6c985]/70">
            {fields.join(" · ")}
        </div>
    );
}

const TYPE_TONES = {
    deposit_opened: "bg-[#22C55E]/15 text-[#22C55E]",
    deposit_settled: "bg-[#22C55E]/15 text-[#22C55E]",
    interest_withdrawn: "bg-[#22C55E]/15 text-[#22C55E]",
    early_exit: "bg-[#C93030]/20 text-[#ff8a8a]",
    abandoned_fee: "bg-[#C93030]/20 text-[#ff8a8a]",
    loan_opened: "bg-[#FBC206]/20 text-[#FBC206]",
    loan_repaid: "bg-[#22C55E]/15 text-[#22C55E]",
    loan_liquidated: "bg-[#C93030]/20 text-[#ff8a8a]",
    compound_executed: "bg-[#FBC206]/20 text-[#FBC206]",
    independent_compound: "bg-[#FBC206]/20 text-[#FBC206]",
    pool_swept: "bg-[#FBC206]/20 text-[#FBC206]",
    reinvestment_settlement: "bg-[#FBC206]/20 text-[#FBC206]",
    deposit_rolled_over: "bg-[#FBC206]/20 text-[#FBC206]",
    nft_transfer_in: "bg-white/15 text-white",
    nft_transfer_out: "bg-white/15 text-white"
};

function prettifyType(type) {
    if (!type) return "Event";
    return type
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Loading-state mirror of the real detail layout. Renders the same card
 * skeleton (header, hero strip, capital routing, position info, loan)
 * with shimmer blocks instead of values so the page doesn't reflow when
 * the deposit row arrives from useDeposits.
 */
function TdDetailSkeleton({ depositId }) {
    return (
        <div className="-mt-6 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-baseline gap-3">
                    <span className="text-[11px] font-semibold text-[#e6c985]/70">My Term Deposits</span>
                    <h1 className="font-manrope text-[22px] font-semibold text-[#e6c985]">
                        Term Deposit #{String(depositId).padStart(6, "0")}
                    </h1>
                    <Shimmer width="4rem" height="1.1rem" />
                </div>
                <Shimmer width="6rem" height="1.5rem" />
            </div>

            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 text-white">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Position Snapshot</h3>
                        <Shimmer width="5rem" height="0.8rem" tone="light" />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="rounded-[8px] bg-white/10 p-3 ring-1 ring-white/10">
                                <Shimmer width="3rem" height="0.6rem" tone="light" />
                                <div className="mt-1.5">
                                    <Shimmer width="4rem" height="1rem" tone="light" />
                                </div>
                                <div className="mt-1">
                                    <Shimmer width="3rem" height="0.6rem" tone="light" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Time to Maturity</h3>
                        <Shimmer width="3rem" height="0.7rem" />
                    </div>
                    <div className="mt-3">
                        <Shimmer width="6rem" height="1.6rem" />
                        <div className="mt-2">
                            <Shimmer width="8rem" height="0.7rem" />
                        </div>
                    </div>
                    <div className="mt-3">
                        <Shimmer width="100%" height="0.5rem" />
                    </div>
                </div>
            </div>

            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Capital Routing</h3>
                    <div className="mt-3">
                        <Shimmer width="100%" height="0.5rem" />
                    </div>
                    <ul className="mt-4 space-y-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <li key={i} className="flex items-center justify-between">
                                <Shimmer width="6rem" height="0.875rem" />
                                <Shimmer width="4rem" height="0.875rem" />
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Lifecycle</h3>
                    <ol className="mt-3 space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <li key={i}>
                                <div className="flex items-baseline justify-between">
                                    <Shimmer width="5rem" height="0.875rem" />
                                    <Shimmer width="4rem" height="0.7rem" />
                                </div>
                                <div className="mt-1.5">
                                    <Shimmer width="80%" height="0.7rem" />
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </div>
    );
}

function formatDate(unix) {
    // Guard against null / 0 — callers pass null for milestones that
    // are no longer applicable (e.g. the Maturity step on an Exited
    // position) and we don't want "Jan 01, 1970" rendered in the slot.
    if (!unix) return "—";
    const d = new Date(Number(unix) * 1000);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function shortAddr(addr) {
    if (!addr) return "—";
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/* -------------------------------------------------------------------------- */
/*  Pieces                                                                    */
/* -------------------------------------------------------------------------- */

function StatePill({ state, label }) {
    // Palette mapping per lifecycle state; falls back to muted for unknowns.
    const map = {
        1: { bg: "#1D3E29", fg: "#FBC206" }, // Active
        2: { bg: "#FBC206", fg: "#1D3E29" }, // Matured
        3: { bg: "#5C7A66", fg: "#FFFFFF" }, // Dormant
        4: { bg: "#C93030", fg: "#FFFFFF" }, // Abandoned
        5: { bg: "#bfb67b", fg: "#1D3E29" }, // Exited
        6: { bg: "#bfb67b", fg: "#1D3E29" } // Closed
    };
    const c = map[state] || { bg: "#5C7A66", fg: "#FFFFFF" };
    return (
        <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
            style={{ backgroundColor: c.bg, color: c.fg }}
        >
            <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: c.fg }}
            />
            {label}
        </span>
    );
}

function Metric({ label, value, hint, accent = false, bold = false }) {
    return (
        <div className="rounded-[8px] bg-white/10 px-3 py-2 ring-1 ring-white/10">
            <div className="text-[8.5px] font-bold uppercase tracking-widest text-white/55">{label}</div>
            <div
                className={`mt-0.5 font-manrope ${bold ? "text-[16px] font-bold" : "text-[14px] font-semibold"} ${
                    accent ? "text-[#FBC206]" : "text-white"
                }`}
            >
                {value}
            </div>
            <div className="mt-0.5 text-[10px] text-white/50">{hint}</div>
        </div>
    );
}

function RouteRow({ color, label, pct, amount, last = false }) {
    return (
        <li className={`flex items-center justify-between ${last ? "" : "border-b border-white/10 pb-3"}`}>
            <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-[12px] font-semibold text-white">{label}</span>
                <span className="text-[10px] font-medium text-[#e6c985]/70">{pct}</span>
            </div>
            <div className="text-right">
                <span className="font-manrope text-[12px] font-bold text-white">{amount}</span>
                <span className="ml-1 text-[9px] font-semibold text-[#e6c985]/70">{TOKEN_SYMBOL}</span>
            </div>
        </li>
    );
}

function TimelineRow({ label, timestamp, description, done = false, last = false }) {
    return (
        <li className="flex gap-3">
            <div className="flex flex-col items-center">
                <span
                    className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                        done ? "bg-[#FBC206] text-[#10281b]" : "bg-white/10 text-[#e6c985]/40 ring-1 ring-[#bfb67b]"
                    }`}
                >
                    {done ? <CheckIcon /> : <DotIcon />}
                </span>
                {!last ? <span className="mt-1 w-px flex-1 bg-white/15" /> : null}
            </div>
            <div className="flex-1 pb-3 last:pb-0">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="font-manrope text-[12px] font-bold text-white">{label}</span>
                    <span className="text-[10px] font-medium text-[#e6c985]/70">{formatDate(timestamp)}</span>
                </div>
                <p className="mt-0.5 text-[10.5px] leading-snug text-[#e6c985]/80">{description}</p>
            </div>
        </li>
    );
}

function InfoRow({ label, value, last = false, mono = false }) {
    return (
        <li className={`flex items-center justify-between gap-3 py-2 ${last ? "" : ""}`}>
            <span className="text-[10.5px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                {label}
            </span>
            <span className={`text-right text-[12px] font-semibold text-white ${mono ? "font-mono text-[10.5px]" : ""}`}>
                {value}
            </span>
        </li>
    );
}

/* -------------------------------------------------------------------------- */
/*  Icons                                                                     */
/* -------------------------------------------------------------------------- */

function ChevronLeft() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13 4 7 10 13 16" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="2.5 6.5 5 9 9.5 3.5" />
        </svg>
    );
}

function DotIcon() {
    return (
        <svg className="h-2 w-2" viewBox="0 0 8 8" aria-hidden="true">
            <circle cx="4" cy="4" r="3" fill="currentColor" />
        </svg>
    );
}

function ExitIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 4 L10 10" />
            <path d="M5.5 6 A6 6 0 1 0 14.5 6" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 3 H13 V7" />
            <path d="M13 3 L7 9" />
            <path d="M11 9 V13 H3 V5 H7" />
        </svg>
    );
}

function ShieldCheckIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3 L20 6 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V6 Z" />
            <polyline points="9 12 11 14 15 10" />
        </svg>
    );
}

/**
 * Rollover modal body — three sections mirroring the Deposit page layout:
 *   A) New Deposit Amount: compound interest vs take interest as USDC.
 *   B) Product: Turbo / Standard selector (now choosable).
 *   C) Automated Reinvestment: ON/OFF toggle.
 */
function RolloverChoice({
    principal,
    accrued,
    compound,
    onChange,
    product,
    onProductChange,
    autoCompound,
    onAutoCompoundChange
}) {
    const compoundTotal = principal + accrued;
    const newDepositAmount = compound ? compoundTotal : principal;

    return (
        <div className="space-y-4">
            {/* ── Section A — New Deposit Amount ─────────────────────────── */}
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <h4 className="font-manrope text-[10px] font-bold uppercase tracking-widest text-[#e6c985]">
                        New Deposit Amount
                    </h4>
                    <span className="font-manrope text-[13px] font-bold text-white">
                        ${formatUsdcCompact(newDepositAmount)}{" "}
                        <span className="text-[9px] font-semibold text-[#e6c985]/70">{TOKEN_SYMBOL}</span>
                    </span>
                </div>
                <div className="space-y-2">
                    <RolloverOption
                        selected={compound}
                        onClick={() => onChange(true)}
                        title="Compound interest"
                        desc={`New deposit: $${formatUsdcCompact(compoundTotal)} — principal + interest rolled in.`}
                    />
                    <RolloverOption
                        selected={!compound}
                        onClick={() => onChange(false)}
                        title="Take interest as USDC"
                        desc={`New deposit: $${formatUsdcCompact(principal)} — $${formatUsdcCompact(
                            accrued
                        )} interest returned to your wallet.`}
                    />
                </div>
                {accrued <= 0n && (
                    <p className="mt-2 rounded-[8px] bg-[#162e1f] px-2.5 py-2 text-[10px] leading-snug text-[#e6c985]/80 ring-1 ring-[#bfb67b]/40">
                        No accrued interest on this position — its yield was already
                        reinvested by Automated Reinvestment (swept into a fresh deposit). Only the
                        ${formatUsdcCompact(principal)} {TOKEN_SYMBOL} principal rolls into the new term; both
                        options above are the same here.
                    </p>
                )}
            </div>

            {/* ── Section B — Product ────────────────────────────────────── */}
            <div>
                <div className="flex items-center justify-between">
                    <h4 className="font-manrope text-[10px] font-bold uppercase tracking-widest text-[#e6c985]">
                        Select Product
                    </h4>
                    <span className="text-[9px] font-medium text-[#e6c985]/70">
                        Pool eligibility at sweep time
                    </span>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                    {ROLLOVER_PRODUCT_OPTIONS.map((opt) => {
                        const active = product === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => onProductChange(opt.value)}
                                className={`rounded-[8px] px-2.5 py-2 text-left ring-1 transition-colors ${
                                    active
                                        ? "bg-[#FBC206] ring-[#FBC206]"
                                        : "bg-[#162e1f] ring-[#bfb67b] hover:bg-white/5"
                                }`}
                            >
                                <div className="flex items-baseline justify-between gap-1">
                                    <span
                                        className={`text-[11px] font-bold ${
                                            active ? "text-[#10281b]" : "text-white"
                                        }`}
                                    >
                                        {opt.label}
                                    </span>
                                    <span
                                        className={`text-[10px] font-semibold ${
                                            active ? "text-[#10281b]" : "text-[#FBC206]"
                                        }`}
                                    >
                                        {opt.rate}
                                    </span>
                                </div>
                                <span
                                    className={`block mt-0.5 text-[8.5px] font-medium leading-tight ${
                                        active ? "text-[#10281b]/70" : "text-[#e6c985]/60"
                                    }`}
                                >
                                    {opt.hint}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <p className="mt-1 text-[9px] text-[#e6c985]/70">
                    {ROLLOVER_PRODUCT_OPTIONS.find((o) => o.value === product)?.copy}
                </p>
            </div>

            {/* ── Section C — Automated Reinvestment ──────────────────────────────── */}
            <div className="flex items-start justify-between gap-2 rounded-[8px] bg-white/5 ring-1 ring-[#bfb67b]/50 px-3 py-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-manrope text-[11px] font-bold text-white">
                            Automated Reinvestment
                        </span>
                        <span
                            className={`text-[9px] font-semibold uppercase tracking-widest ${
                                autoCompound ? "text-[#22C55E]" : "text-[#e6c985]/60"
                            }`}
                        >
                            {autoCompound ? "On" : "Off"}
                        </span>
                    </div>
                    <p className="mt-0.5 text-[9.5px] leading-snug text-[#e6c985]/75">
                        {autoCompound
                            ? "When pool yield reaches $100, the keeper sweeps and mints a new 3-year Term Deposit for you."
                            : "Interest accrues in your deposit. Withdraw penalty-free at maturity; exiting earlier forfeits the early-exit penalty."}
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={autoCompound}
                    onClick={() => onAutoCompoundChange((prev) => !prev)}
                    className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                        autoCompound ? "bg-[#FBC206]" : "bg-white/15"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            autoCompound ? "translate-x-[18px]" : "translate-x-0.5"
                        }`}
                    />
                </button>
            </div>
        </div>
    );
}

function RolloverOption({ selected, onClick, title, desc }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-start gap-2.5 rounded-[10px] px-3 py-2.5 text-left ring-1 transition-colors ${
                selected
                    ? "bg-[#FBC206]/15 ring-[#FBC206]"
                    : "bg-white/5 ring-[#bfb67b]/50 hover:bg-white/10"
            }`}
        >
            <span
                className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-1 ${
                    selected ? "bg-[#FBC206] ring-[#FBC206]" : "ring-[#bfb67b]"
                }`}
            >
                {selected ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10281b]" />
                ) : null}
            </span>
            <span>
                <span className="block font-manrope text-[12px] font-bold text-white">{title}</span>
                <span className="mt-0.5 block text-[10.5px] text-[#e6c985]/75">{desc}</span>
            </span>
        </button>
    );
}

/**
 * "Create Loan" CTA for the Loan Position card. Goes straight to the
 * Lending page — V6.2 strict removes the per-NFT Turbo loop entry
 * point from this surface (Turbo is selected at deposit creation
 * via the productMode picker, not after-the-fact). Label matches the
 * My Loans page's "Create Loan" button for consistency.
 */
function BorrowMenu() {
    return (
        <Link
            to="/loans/lending"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FBC206] px-4 py-1.5 text-[11px] font-semibold text-[#10281b] hover:bg-[#e6b005] transition-colors"
        >
            Create Loan
            <ChevronRight />
        </Link>
    );
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Polls `bond.getDeposit(depositId)` until the new deposit row is
 * visible from the wallet's RPC, or `timeoutMs` elapses. Used after a
 * rollover so the follow-up `autoCompound.setCompoundMode` doesn't
 * estimate-gas against a stale block where `_existingDeposit` would
 * revert. Best-effort: on timeout we return false and let the caller
 * attempt anyway rather than hard-failing.
 */
async function waitForDepositVisible(bond, depositId, timeoutMs = 20000, intervalMs = 1000) {
    const ZERO = "0x0000000000000000000000000000000000000000";
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        try {
            const d = await bond.getDeposit(depositId);
            if (d && d.owner && d.owner.toLowerCase() !== ZERO) return true;
        } catch {
            /* deposit not visible on this node yet — keep polling */
        }
        await delay(intervalMs);
    }
    return false;
}
